/**
 * ============================================================
 * STICKY ADD TO CART — Em & Me Studio
 * File: assets/sticky-atc.js
 * Compatible with: Bold Product Options, Shopify Dawn / custom themes
 *
 * Behaviour:
 *  - Personalised products (tag: "personalized"):
 *      → Scroll to Bold fields + highlight if empty
 *      → Once filled: fire native ATC (preserves Bold properties)
 *  - Non-personalised products:
 *      → Direct /cart/add.js fetch, no scroll needed
 *  - Syncs variant ID + price when swatches change
 *  - Hides bar when native ATC button is visible on screen
 * ============================================================
 */

(function () {
  'use strict';

  /* ── DOM refs ─────────────────────────────────────────────── */
  const bar  = document.getElementById('sticky-atc-bar');
  const btn  = document.getElementById('satc-btn');
  const hint = document.getElementById('satc-hint');

  // Bail early if bar not on this page
  if (!bar || !btn) return;

  /* ── Config ───────────────────────────────────────────────── */
  const CONFIG = {
    showAfterPx      : 300,   // scroll distance before bar appears
    stickyHeaderPx   : 80,    // offset for any fixed header when scrolling to fields
    successResetMs   : 2200,  // ms before "Added!" resets to original label
    errorResetMs     : 2500,
    boldFieldSelector: '.bold_options input[name^="properties["], .bold_options textarea[name^="properties["]',
    boldOptionWrapper: '.bold_option',
    boldContainer    : '.bold_options',
    nativeATCSelector: 'button[name="add"], [type="submit"][name="add"]',
    variantSelector  : 'select[name="id"], input[name="id"]:checked',
    priceSelectors   : [
      '.price__regular .price-item--regular',   // Dawn
      '.product__price .money',                  // Debut / vintage
      '.price-item--regular',                    // generic
      '[data-product-price]',                    // some custom themes
    ],
  };

  /* ── State ────────────────────────────────────────────────── */
  const IS_PERSONALIZED = btn.dataset.personalized === 'true';
  let   isBarVisible    = false;
  let   isReady         = false;   // true once all Bold fields are filled
  let   scrollRAF       = null;

  /* ── Helpers ──────────────────────────────────────────────── */

  /**
   * Return the native ATC button (may be inside a form or shadow DOM)
   */
  function getNativeATCBtn() {
    return document.querySelector(CONFIG.nativeATCSelector);
  }

  /**
   * Return all Bold text/textarea property inputs
   */
  function getBoldInputs() {
    return Array.from(document.querySelectorAll(CONFIG.boldFieldSelector));
  }

  /**
   * Return Bold inputs that are required AND empty
   * Bold marks required fields via: required attr, data-required, OR
   * a required class on the parent .bold_option
   */
  function getEmptyRequiredBoldFields() {
    return getBoldInputs().filter(function (el) {
      if (!el.value.trim()) {
        const wrapper = el.closest(CONFIG.boldOptionWrapper);
        return (
          el.required ||
          el.dataset.required === 'true' ||
          (wrapper && (
            wrapper.dataset.required === 'true' ||
            wrapper.classList.contains('bold_option_required')
          ))
        );
      }
      return false;
    });
  }

  /**
   * Are ALL required Bold fields filled?
   */
  function allBoldFieldsFilled() {
    return getEmptyRequiredBoldFields().length === 0;
  }

  /**
   * Get current page price text from the first matching selector
   */
  function getPagePrice() {
    for (let i = 0; i < CONFIG.priceSelectors.length; i++) {
      const el = document.querySelector(CONFIG.priceSelectors[i]);
      if (el && el.textContent.trim()) {
        return el.textContent.trim();
      }
    }
    return null;
  }

  /**
   * Update the price in the sticky bar
   */
  function syncPrice() {
    const priceEl = document.getElementById('satc-price');
    if (!priceEl) return;
    const price = getPagePrice();
    if (price) priceEl.textContent = price;
  }

  /**
   * Update variant ID in the button's data attribute
   */
  function syncVariantId() {
    const sel = document.querySelector(CONFIG.variantSelector);
    if (sel) btn.dataset.variantId = sel.value;
  }

  /* ── Bar visibility ───────────────────────────────────────── */

  function showBar() {
    if (isBarVisible) return;
    bar.classList.add('satc--visible');
    isBarVisible = true;
  }

  function hideBar() {
    if (!isBarVisible) return;
    bar.classList.remove('satc--visible');
    isBarVisible = false;
  }

  /**
   * Is the native ATC button currently visible in the viewport?
   */
  function isNativeATCVisible() {
    const nativeBtn = getNativeATCBtn();
    if (!nativeBtn) return false;
    const rect = nativeBtn.getBoundingClientRect();
    return rect.top >= 0 && rect.bottom <= window.innerHeight;
  }

  function handleScroll() {
    if (scrollRAF) return; // throttle to rAF
    scrollRAF = requestAnimationFrame(function () {
      scrollRAF = null;

      if (isNativeATCVisible() || window.scrollY < CONFIG.showAfterPx) {
        hideBar();
      } else {
        showBar();
        // Update ready state each scroll so button label stays accurate
        if (IS_PERSONALIZED) updateReadyState();
      }
    });
  }

  /* ── Ready state (personalised only) ─────────────────────── */

  /**
   * Toggle the button between "Personalise & Add to Cart" and "Add to Cart"
   * based on whether Bold fields are filled
   */
  function updateReadyState() {
    const filled = allBoldFieldsFilled();
    const boldExists = document.querySelector(CONFIG.boldContainer);

    // If no Bold container exists on this product, treat as ready
    const ready = !boldExists || filled;

    if (ready === isReady) return; // no change
    isReady = ready;

    const textEl = btn.querySelector('.satc__btn-text');
    const iconEl = btn.querySelector('.satc__btn-icon');

    if (ready) {
      btn.classList.add('satc__btn--ready');
      btn.classList.remove('satc__btn--personalized');
      btn.classList.add('satc__btn--direct'); // visually looks like "add"
      if (textEl) textEl.textContent = 'Add to Cart';
      if (iconEl) iconEl.textContent = '→';
      if (hint) hint.classList.remove('satc--hint-visible');
    } else {
      btn.classList.remove('satc__btn--ready');
      btn.classList.add('satc__btn--personalized');
      btn.classList.remove('satc__btn--direct');
      if (textEl) textEl.textContent = 'Personalise & Add to Cart';
      if (iconEl) iconEl.textContent = '↓';
    }
  }

  /* ── Scroll to & highlight Bold fields ───────────────────── */

  function scrollToBoldFields() {
    const container = document.querySelector(CONFIG.boldContainer);
    if (!container) return;

    const top = container.getBoundingClientRect().top + window.scrollY - CONFIG.stickyHeaderPx;
    window.scrollTo({ top: top, behavior: 'smooth' });
  }

  function highlightEmptyFields(emptyFields) {
    // Show hint strip
    if (hint) hint.classList.add('satc--hint-visible');

    emptyFields.forEach(function (input) {
      const wrapper = input.closest(CONFIG.boldOptionWrapper) || input;

      // Remove then re-add to restart CSS animation
      wrapper.classList.remove('satc-field-highlight');
      void wrapper.offsetWidth; // force reflow
      wrapper.classList.add('satc-field-highlight');

      // Clean up after animation ends
      const cleanup = function () {
        wrapper.classList.remove('satc-field-highlight');
      };
      wrapper.addEventListener('animationend', cleanup, { once: true });
    });

    // Focus first empty field for accessibility
    emptyFields[0].focus();
  }

  /* ── Button state helpers ─────────────────────────────────── */

  function setBtnLoading() {
    const textEl = btn.querySelector('.satc__btn-text');
    if (textEl) textEl.textContent = 'Adding...';
    btn.disabled = true;
  }

  function setBtnSuccess() {
    btn.classList.add('satc__btn--success');
    const textEl = btn.querySelector('.satc__btn-text');
    if (textEl) textEl.textContent = '✓ Added to Cart!';
    btn.disabled = false;
  }

  function setBtnError(msg) {
    btn.classList.add('satc__btn--error');
    const textEl = btn.querySelector('.satc__btn-text');
    if (textEl) textEl.textContent = msg || 'Error — Try Again';
    btn.disabled = false;
  }

  function resetBtn(label) {
    btn.classList.remove('satc__btn--success', 'satc__btn--error');
    btn.disabled = false;
    const textEl = btn.querySelector('.satc__btn-text');
    if (textEl) textEl.textContent = label;
  }

  /* ── Click handlers ───────────────────────────────────────── */

  function handlePersonalizedClick() {
    const emptyFields = getEmptyRequiredBoldFields();
    const boldExists  = document.querySelector(CONFIG.boldContainer);

    if (boldExists && emptyFields.length > 0) {
      // Fields unfilled — scroll + highlight
      scrollToBoldFields();
      highlightEmptyFields(emptyFields);
    } else {
      // All filled (or no Bold fields) — submit via native button
      submitViaNativeATC();
    }
  }

  /**
   * Trigger the native Shopify ATC button.
   * This is REQUIRED for personalised products because Bold attaches
   * all properties[...] values to the form submit — a raw fetch() would
   * miss them and produce an order with no personalisation data.
   */
  function submitViaNativeATC() {
    const nativeBtn = getNativeATCBtn();
    if (nativeBtn) {
      nativeBtn.click();
      // After clicking native, watch for cart update events to give feedback
      listenForCartUpdate();
    } else {
      console.warn('[Sticky ATC] Native ATC button not found.');
    }
  }

  /**
   * Listen for common theme cart-update events to update sticky button state.
   * Different themes fire different events — cover them all.
   */
  function listenForCartUpdate() {
    setBtnLoading();

    const events = ['cart:updated', 'cart:add', 'ajaxCart:added', 'theme:cart:added'];
    const cleanup = [];

    const onSuccess = function () {
      setBtnSuccess();
      cleanup.forEach(function (fn) { fn(); });
      setTimeout(function () {
        resetBtn(IS_PERSONALIZED && !allBoldFieldsFilled()
          ? 'Personalise & Add to Cart'
          : 'Add to Cart');
      }, CONFIG.successResetMs);
    };

    events.forEach(function (evtName) {
      const handler = onSuccess;
      document.addEventListener(evtName, handler, { once: true });
      cleanup.push(function () { document.removeEventListener(evtName, handler); });
    });

    // Fallback: if no cart event fires within 4 seconds, reset button
    const fallback = setTimeout(function () {
      cleanup.forEach(function (fn) { fn(); });
      resetBtn(IS_PERSONALIZED ? 'Personalise & Add to Cart' : 'Add to Cart');
    }, 4000);
    cleanup.push(function () { clearTimeout(fallback); });
  }

  /**
   * Direct /cart/add.js fetch for non-personalised products.
   * Safe to use here because there are no Bold line-item properties needed.
   */
  function handleDirectATC() {
    const variantId = parseInt(btn.dataset.variantId, 10);
    if (!variantId) {
      console.warn('[Sticky ATC] No variant ID found on button.');
      return;
    }

    setBtnLoading();

    fetch('/cart/add.js', {
      method : 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body   : JSON.stringify({ id: variantId, quantity: 1 }),
    })
      .then(function (res) {
        if (!res.ok) return res.json().then(function (d) { throw new Error(d.description || 'Error'); });
        return res.json();
      })
      .then(function () {
        setBtnSuccess();

        // Notify theme of cart change (opens drawer, updates count, etc.)
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('ajaxCart:added',  { bubbles: true }));

        // Also update the Shopify cart count badge if theme exposes it
        refreshCartCount();

        setTimeout(function () {
          resetBtn('Add to Cart');
        }, CONFIG.successResetMs);
      })
      .catch(function (err) {
        console.error('[Sticky ATC]', err);
        setBtnError('Could not add — Try Again');
        setTimeout(function () {
          resetBtn('Add to Cart');
        }, CONFIG.errorResetMs);
      });
  }

  /**
   * Refresh the cart item count badge via /cart.js
   * Works across Dawn, Debut and most custom themes that use
   * a [data-cart-count] or .cart-count element.
   */
  function refreshCartCount() {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        const count = cart.item_count;
        const selectors = [
          '[data-cart-count]',
          '.cart-count',
          '.cart-item-count',
          '.header__cart-count',
          '#CartCount',
        ];
        selectors.forEach(function (sel) {
          document.querySelectorAll(sel).forEach(function (el) {
            el.textContent = count;
            if (count > 0) el.removeAttribute('hidden');
          });
        });
      })
      .catch(function () { /* non-critical */ });
  }

  /* ── Event: main button click ─────────────────────────────── */

  btn.addEventListener('click', function () {
    if (IS_PERSONALIZED) {
      handlePersonalizedClick();
    } else {
      handleDirectATC();
    }
  });

  /* ── Event: scroll ────────────────────────────────────────── */

  window.addEventListener('scroll', handleScroll, { passive: true });

  /* ── Event: variant / swatch changes ─────────────────────── */

  // Native Shopify variant selects / radio inputs
  document.addEventListener('change', function (e) {
    const target = e.target;

    // Variant selector changed
    if (target.name === 'id' || target.closest('[data-section-type="product"]')) {
      syncVariantId();
      // Small delay — theme JS updates the price after the event
      setTimeout(syncPrice, 80);
    }

    // Bold field changed — update ready state
    if (target.name && target.name.startsWith('properties[')) {
      if (IS_PERSONALIZED) updateReadyState();
      // Also clear highlight from this field's wrapper
      const wrapper = target.closest(CONFIG.boldOptionWrapper);
      if (wrapper) wrapper.classList.remove('satc-field-highlight');
      // Hide hint if all done
      if (hint && allBoldFieldsFilled()) hint.classList.remove('satc--hint-visible');
    }
  });

  // Bold also fires input events for textarea live typing
  document.addEventListener('input', function (e) {
    const target = e.target;
    if (target.name && target.name.startsWith('properties[')) {
      if (IS_PERSONALIZED) updateReadyState();
      if (hint && allBoldFieldsFilled()) hint.classList.remove('satc--hint-visible');
    }
  });

  /* ── Event: Bold's own events ─────────────────────────────── */

  // Bold fires this after it finishes rendering options & pricing
  document.addEventListener('BOLD_OPTIONS_loaded', function () {
    syncPrice();
    if (IS_PERSONALIZED) updateReadyState();
  });

  // Bold fires this when priced options change the total
  document.addEventListener('BOLD_OPTIONS_price_updated', function () {
    syncPrice();
  });

  // Fallback: MutationObserver on Bold price if events aren't fired
  (function observeBoldPrice() {
    const boldContainer = document.querySelector('.bold_options');
    if (!boldContainer) return;

    const observer = new MutationObserver(function () {
      syncPrice();
      if (IS_PERSONALIZED) updateReadyState();
    });

    observer.observe(boldContainer, {
      childList : true,
      subtree   : true,
      attributes: false,
    });
  })();

  /* ── Init ─────────────────────────────────────────────────── */

  // Set initial variant ID (may differ from Liquid-rendered one if URL has variant param)
  syncVariantId();

  // Run initial scroll check in case page is loaded mid-scroll
  handleScroll();

  // Initial ready state check
  if (IS_PERSONALIZED) updateReadyState();

})();
